/**
 * Runtime map of Liquidity icon export names → React components for artifact icons.
 * Keep registry keys in sync with CMT `marketingMessageIconAllowlist.ts` (`CAMPAIGN_ICON_REGISTRY`).
 * Allowlist strings must match `packages/liquidity/src/icons/index.ts` export names exactly.
 * Unknown or missing artifact keys fall back to LightBulbGearIcon (marketing) or BoxCheckDuotoneIcon (nav).
 *
 * Client-widget-only icons (`CalendarClockIcon`) are imported directly in `useClientMarketingWidget` — not registered here.
 */
import type { ComponentType, SVGProps } from 'react';
import {
  AwardIcon,
  BallotCheckIcon,
  BoxCheckDuotoneIcon,
  BriefcaseDuotoneIcon,
  BuildingColumnsIconDuotone,
  CalculatorIcon,
  ChartDoubleLineIcon,
  ChartLineIcon,
  CircleCheckIcon,
  ClipboardClockIcon,
  ClockEightDuotoneIcon,
  Coins2Icon,
  EnvelopeOpenDollarIcon,
  FastBillIcon,
  FileInvoiceDuotoneIcon,
  GrowFundsIcon,
  HandBill2Icon,
  HandshakeIcon,
  InvoiceCalculateIcon,
  InvoiceIcon,
  InvoicesIcon,
  LightBulbGearIcon,
  PaperBillsIcon,
  PercentageClockIcon,
  PercentageDownIcon,
  PercentageIcon,
  PointTapIcon,
  RabbitFastIcon,
  RotateBillIcon,
  ShieldHalvedIcon,
  StackBillsIcon,
  TicketIcon,
  TypingIcon,
  UploadIcon,
  WalletIcon,
} from '@c2fo/liquidity/icons';

export const MARKETING_MESSAGE_ICON_ALLOWLIST = [
  'ChartDoubleLineIcon',
  'ChartLineIcon',
  'CircleCheckIcon',
  'ClipboardClockIcon',
  'Coins2Icon',
  'FastBillIcon',
  'HandBill2Icon',
  'HandshakeIcon',
  'GrowFundsIcon',
  'InvoiceCalculateIcon',
  'InvoiceIcon',
  'InvoicesIcon',
  'LightBulbGearIcon',
  'PaperBillsIcon',
  'PercentageIcon',
  'PercentageClockIcon',
  'PercentageDownIcon',
  'PointTapIcon',
  'RabbitFastIcon',
  'RotateBillIcon',
  'StackBillsIcon',
  'TicketIcon',
  'TypingIcon',
  'UploadIcon',
  'WalletIcon',
] as const;

/** Nav artifact icon keys — same strings as CMT `NAVIGATION_ASSET_ICON_ALLOWLIST` / Liquidity exports. */
export const NAVIGATION_ARTIFACT_ICON_ALLOWLIST = [
  'AwardIcon',
  'BallotCheckIcon',
  'BoxCheckDuotoneIcon',
  'BriefcaseDuotoneIcon',
  'BuildingColumnsIconDuotone',
  'CalculatorIcon',
  'ClockEightDuotoneIcon',
  'EnvelopeOpenDollarIcon',
  'FileInvoiceDuotoneIcon',
  'ShieldHalvedIcon',
] as const;

export type MarketingMessageIconKey = (typeof MARKETING_MESSAGE_ICON_ALLOWLIST)[number];
export type NavigationArtifactIconKey = (typeof NAVIGATION_ARTIFACT_ICON_ALLOWLIST)[number];
export type CampaignIconKey = MarketingMessageIconKey | NavigationArtifactIconKey;

export const DEFAULT_MARKETING_MESSAGE_ICON_KEY: MarketingMessageIconKey = 'LightBulbGearIcon';

type LiquidityIconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** Maps persisted artifact icon keys to Liquidity React components. */
export const MARKETING_MESSAGE_ICON_REGISTRY: Record<CampaignIconKey, LiquidityIconComponent> = {
  ChartDoubleLineIcon,
  ChartLineIcon,
  CircleCheckIcon,
  ClipboardClockIcon,
  Coins2Icon,
  FastBillIcon,
  HandBill2Icon,
  HandshakeIcon,
  GrowFundsIcon,
  InvoiceCalculateIcon,
  InvoiceIcon,
  InvoicesIcon,
  LightBulbGearIcon,
  PaperBillsIcon,
  PercentageClockIcon,
  PercentageIcon,
  PercentageDownIcon,
  PointTapIcon,
  RabbitFastIcon,
  RotateBillIcon,
  StackBillsIcon,
  TicketIcon,
  TypingIcon,
  UploadIcon,
  WalletIcon,
  AwardIcon,
  BallotCheckIcon,
  BoxCheckDuotoneIcon,
  BriefcaseDuotoneIcon,
  BuildingColumnsIconDuotone,
  CalculatorIcon,
  ClockEightDuotoneIcon,
  EnvelopeOpenDollarIcon,
  FileInvoiceDuotoneIcon,
  ShieldHalvedIcon,
};

export function resolveMarketingMessageIconKey(iconKey: string | undefined): MarketingMessageIconKey {
  if (iconKey && Object.hasOwn(MARKETING_MESSAGE_ICON_REGISTRY, iconKey)) {
    const key = iconKey as CampaignIconKey;
    if (MARKETING_MESSAGE_ICON_ALLOWLIST.includes(key as MarketingMessageIconKey)) {
      return key as MarketingMessageIconKey;
    }
  }

  return DEFAULT_MARKETING_MESSAGE_ICON_KEY;
}

export function resolveMarketingMessageIcon(iconKey: string | undefined): LiquidityIconComponent {
  return MARKETING_MESSAGE_ICON_REGISTRY[resolveMarketingMessageIconKey(iconKey)];
}

export function resolveCampaignIconKey(iconKey: string | undefined): CampaignIconKey {
  if (iconKey && Object.hasOwn(MARKETING_MESSAGE_ICON_REGISTRY, iconKey)) {
    return iconKey as CampaignIconKey;
  }

  return DEFAULT_MARKETING_MESSAGE_ICON_KEY;
}

export function resolveCampaignIcon(iconKey: string | undefined): LiquidityIconComponent {
  return MARKETING_MESSAGE_ICON_REGISTRY[resolveCampaignIconKey(iconKey)];
}
